import { Typography } from 'app/components/ui';
import { i18n } from 'app/utils/i18n';
import { RegionRow, TableRow } from 'app/api/coverage-types';

interface Props {
  regions: RegionRow[];
  tables: TableRow[];
  onSelect: (row: RegionRow | TableRow) => void;
  regionsEmpty: boolean;
  tablesEmpty: boolean;
  allEmpty: boolean;
  regionsPagination?: React.ReactNode;
  tablesPagination?: React.ReactNode;
}

const statusLabelKey = (s: RegionRow['status']) => `tag.coverage.status.${s}`;
const ruleLabelKey = (k: string) => `tag.coverage.rule.${k === 'all-cells' ? 'allCells' : k}.short`;

export const CoverageOverviewTable = ({ regions, tables, onSelect, regionsEmpty, tablesEmpty, allEmpty, regionsPagination, tablesPagination }: Props) => {
  if (allEmpty) {
    return (
      <div className="coverage-overview-table__empty-all">
        <Typography variant="body-5">{i18n('tag.coverage.overview.emptyState')}</Typography>
      </div>
    );
  }

  return (
    <div className="coverage-overview-table">
      <section>
        <header className="coverage-overview-table__section-header">
          <Typography variant="body-5" fontWeight="bold">
            {i18n('tag.coverage.section.regions')}
          </Typography>
        </header>
        {regionsEmpty ? (
          <div className="coverage-overview-table__empty">
            <Typography variant="body-5">{i18n('tag.coverage.section.regionsEmpty')}</Typography>
          </div>
        ) : (
          <>
            <table className="coverage-overview-table__rows">
              <thead>
                <tr>
                  <th>{i18n('tag.coverage.col.anchor')}</th>
                  <th>{i18n('tag.coverage.col.section')}</th>
                  <th>{i18n('tag.coverage.col.page')}</th>
                  <th>{i18n('tag.coverage.col.rule')}</th>
                  <th>{i18n('tag.coverage.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {regions.map((r) => (
                  <tr key={`${r.sectionSuperId}-${r.anchorPath.join('.')}`} onClick={() => onSelect(r)} className="coverage-overview-table__row">
                    <td>{r.heading || i18n('tag.coverage.heading.fallback')}</td>
                    <td>{r.sectionName || r.sectionSuperId}</td>
                    <td>{r.page}</td>
                    <td>
                      <span className={`coverage-rule-badge coverage-rule-badge--${r.rule.kind}`}>{i18n(ruleLabelKey(r.rule.kind))}</span>
                    </td>
                    <td>
                      <span className={`coverage-status-badge coverage-status-badge--${r.status}`}>{i18n(statusLabelKey(r.status))}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {regionsPagination && <div className="coverage-overview-table__pagination">{regionsPagination}</div>}
          </>
        )}
      </section>

      <section>
        <header className="coverage-overview-table__section-header">
          <Typography variant="body-5" fontWeight="bold">
            {i18n('tag.coverage.section.tables')}
          </Typography>
        </header>
        {tablesEmpty ? (
          <div className="coverage-overview-table__empty">
            <Typography variant="body-5">{i18n('tag.coverage.section.tablesEmpty')}</Typography>
          </div>
        ) : (
          <>
            <table className="coverage-overview-table__rows">
              <thead>
                <tr>
                  <th>{i18n('tag.coverage.col.table')}</th>
                  <th>{i18n('tag.coverage.col.section')}</th>
                  <th>{i18n('tag.coverage.col.page')}</th>
                  <th>{i18n('tag.coverage.col.status')}</th>
                  <th>{i18n('tag.coverage.col.missingCells')}</th>
                </tr>
              </thead>
              <tbody>
                {tables.map((t) => (
                  <tr key={`${t.sectionSuperId}-${t.tableNodePath.join('.')}`} onClick={() => onSelect(t)} className="coverage-overview-table__row">
                    <td>{t.tableName || i18n('tag.coverage.table.fallbackName')}</td>
                    <td>{t.sectionName || t.sectionSuperId}</td>
                    <td>{t.page}</td>
                    <td>
                      <span className={`coverage-status-badge coverage-status-badge--${t.status}`}>{i18n(statusLabelKey(t.status))}</span>
                    </td>
                    <td>{t.missingCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {tablesPagination && <div className="coverage-overview-table__pagination">{tablesPagination}</div>}
          </>
        )}
      </section>
    </div>
  );
};
